import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { eventsApi } from '../../../api/adminApi';
import {
  eventId,
  eventDescription,
  eventDateInputValue,
  formatEventDateShort,
  todayLocalISODate,
} from './eventHelpers';
import './EventsList.css';

const DEFAULT_LIMIT = 100;
const DELETE_ALL_PHRASE = 'DELETE ALL EVENTS';

function previewText(body, max = 100) {
  const s = (body || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s || '—';
  return `${s.slice(0, max)}…`;
}

function EventsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formEventDate, setFormEventDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllPhrase, setDeleteAllPhrase] = useState('');
  const [deleteAllBusy, setDeleteAllBusy] = useState(false);

  const loadList = (pageNum = page) => {
    setLoading(true);
    setError('');
    eventsApi
      .list(pageNum, limit)
      .then((res) => {
        setItems(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load events'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingItem(null);
    setFormTitle('');
    setFormDescription('');
    setFormEventDate(todayLocalISODate());
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (row) => {
    const eid = eventId(row);
    if (!eid) return;
    setEditingItem(row);
    setFormTitle(row.title ?? '');
    setFormDescription(eventDescription(row));
    setFormEventDate(eventDateInputValue(row) || todayLocalISODate());
    setFormError('');
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingItem(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!formEventDate || !/^\d{4}-\d{2}-\d{2}$/.test(formEventDate)) {
      setFormError('Event date is required (use a valid date).');
      return;
    }

    const payload = {
      title: formTitle.trim(),
      description: formDescription,
      event_date: formEventDate,
    };

    if (editingItem) {
      const eid = eventId(editingItem);
      if (!eid) return;
      const nextTitle = formTitle.trim();
      const nextDesc = formDescription;
      const nextDate = formEventDate;
      const prevTitle = (editingItem.title ?? '').trim();
      const prevDesc = eventDescription(editingItem);
      const prevDate = eventDateInputValue(editingItem);
      if (nextTitle === prevTitle && nextDesc === prevDesc && nextDate === prevDate) {
        setFormError('Change the title, description, or date before saving.');
        return;
      }
      setFormSaving(true);
      eventsApi
        .update(eid, payload)
        .then(() => {
          closeFormModal();
          loadList(page);
        })
        .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
        .finally(() => setFormSaving(false));
      return;
    }

    setFormSaving(true);
    eventsApi
      .create(payload)
      .then(() => {
        closeFormModal();
        loadList(page);
      })
      .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
      .finally(() => setFormSaving(false));
  };

  const handleDeleteClick = (row) => setDeleteTarget(row);
  const handleDeleteCancel = () => setDeleteTarget(null);
  const handleDeleteConfirm = () => {
    const id = eventId(deleteTarget);
    if (!id) return;
    setDeleting(true);
    eventsApi
      .delete(id)
      .then(() => {
        setDeleteTarget(null);
        loadList(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const openDeleteAll = () => {
    setDeleteAllPhrase('');
    setDeleteAllOpen(true);
  };

  const closeDeleteAll = () => {
    if (deleteAllBusy) return;
    setDeleteAllOpen(false);
    setDeleteAllPhrase('');
  };

  const handleDeleteAllConfirm = () => {
    if (deleteAllPhrase.trim() !== DELETE_ALL_PHRASE) return;
    setDeleteAllBusy(true);
    setError('');
    eventsApi
      .deleteAll()
      .then(() => {
        closeDeleteAll();
        loadList(1);
      })
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete all events');
        closeDeleteAll();
      })
      .finally(() => setDeleteAllBusy(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isEditing = editingItem != null;

  return (
    <div className="admin-events">
      <div className="admin-events-header">
        <h2>Events</h2>
        <div className="admin-events-header-actions">
          <button
            type="button"
            className="admin-events-delete-all"
            onClick={openDeleteAll}
            disabled={loading || items.length === 0}
          >
            Delete all
          </button>
          <button type="button" className="admin-events-add" onClick={openAddModal}>
            Add event
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-events-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-events-loading">Loading events…</div>
      ) : (
        <div className="admin-events-table-wrap">
          <table className="admin-events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Preview</th>
                <th>Event date</th>
                <th>Created</th>
                <th className="admin-events-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-events-empty">
                    No events yet.{' '}
                    <button type="button" className="admin-events-empty-btn" onClick={openAddModal}>
                      Add one
                    </button>
                    .
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const eid = eventId(row);
                  const body = eventDescription(row);
                  return (
                    <tr key={eid || row.title}>
                      <td className="admin-events-title-cell">{row.title || '—'}</td>
                      <td className="admin-events-preview-cell">{previewText(body)}</td>
                      <td className="admin-events-date-cell">{formatEventDateShort(eventDateInputValue(row))}</td>
                      <td className="admin-events-date-cell">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="admin-events-actions-col">
                        {eid ? (
                          <Link to={`/admin/events/${eid}`} className="admin-events-btn admin-events-btn-view">
                            View
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          className="admin-events-btn admin-events-btn-edit"
                          onClick={() => openEditModal(row)}
                          disabled={!eid}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-events-btn admin-events-btn-delete"
                          onClick={() => handleDeleteClick(row)}
                          disabled={!eid}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-events-pagination">
          <button
            type="button"
            className="admin-events-page-btn"
            onClick={() => loadList(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-events-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-events-page-btn"
            onClick={() => loadList(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onRequestClose={() => {
          if (!formSaving) closeFormModal();
        }}
        contentLabel={isEditing ? 'Edit event' : 'Add event'}
        className="admin-events-modal-content"
        overlayClassName="admin-events-modal-overlay"
      >
        <div className="admin-events-modal-inner">
          <h3>{isEditing ? 'Edit event' : 'Add event'}</h3>
          {isEditing && (
            <p className="admin-events-modal-lead">Update the title, description, or event date, then save.</p>
          )}
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-events-form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="admin-events-form-field">
              <label htmlFor="ev-form-title">Title *</label>
              <input
                id="ev-form-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Event name"
                disabled={formSaving}
              />
            </div>

            <div className="admin-events-form-field">
              <label htmlFor="ev-form-description">Description</label>
              <textarea
                id="ev-form-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Details, time, location…"
                rows={5}
                disabled={formSaving}
              />
            </div>

            <div className="admin-events-form-field">
              <label htmlFor="ev-form-date">Event date *</label>
              <input
                id="ev-form-date"
                type="date"
                value={formEventDate}
                onChange={(e) => setFormEventDate(e.target.value)}
                disabled={formSaving}
              />
            </div>

            <div className="admin-events-form-actions">
              <button
                type="button"
                className="admin-events-form-cancel"
                onClick={closeFormModal}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button type="submit" className="admin-events-form-submit" disabled={formSaving}>
                {formSaving ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save changes' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteTarget && (
        <div className="admin-events-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ev-del-title">
          <div className="admin-events-delete-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-events-delete-box">
            <h3 id="ev-del-title">Delete event?</h3>
            <p>
              This soft-deletes &ldquo;{deleteTarget.title || 'this event'}&rdquo;. This cannot be undone from the admin
              UI.
            </p>
            <div className="admin-events-delete-actions">
              <button
                type="button"
                className="admin-events-btn admin-events-btn-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-events-btn admin-events-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAllOpen && (
        <div className="admin-events-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ev-del-all-title">
          <div className="admin-events-delete-backdrop" onClick={closeDeleteAll} aria-hidden="true" />
          <div className="admin-events-delete-box admin-events-delete-box--wide">
            <h3 id="ev-del-all-title">Delete all events?</h3>
            <p>
              This calls <code className="admin-events-code">DELETE /admin/events</code> and soft-deletes every event.
              Type the phrase below exactly to confirm.
            </p>
            <label className="admin-events-delete-all-label" htmlFor="ev-delete-all-phrase">
              Type: <strong>{DELETE_ALL_PHRASE}</strong>
            </label>
            <input
              id="ev-delete-all-phrase"
              type="text"
              className="admin-events-delete-all-input"
              value={deleteAllPhrase}
              onChange={(e) => setDeleteAllPhrase(e.target.value)}
              autoComplete="off"
              disabled={deleteAllBusy}
            />
            <div className="admin-events-delete-actions">
              <button
                type="button"
                className="admin-events-btn admin-events-btn-cancel"
                onClick={closeDeleteAll}
                disabled={deleteAllBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-events-btn admin-events-btn-delete"
                onClick={handleDeleteAllConfirm}
                disabled={deleteAllBusy || deleteAllPhrase.trim() !== DELETE_ALL_PHRASE}
              >
                {deleteAllBusy ? 'Deleting…' : 'Delete all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventsList;
